import { FC, useEffect, useRef, useState } from 'react'
import ValidatorWrapper, { rules, ValidatorField } from '@coxy/react-validator'
import cn from 'classnames'

import { constants } from 'config/constants'
import validatorRules from 'libs/validator-rules'
import { useAppDispatch } from 'store/hooks'
import { setFallbackEmail, updateUser } from 'store/fetchers/users'
import { fetchUser } from 'store/reducers/users'
import {
  EMessageTypes,
  getVerifiedToken,
  resendVerificationCode,
  sendVerificationCode,
} from 'store/fetchers/utils'
import { InputEmail } from 'components/app/input-email'
import InputPhone from 'components/app/input-phone'
import { Captcha } from 'components/app/captcha'
import InputCode from 'components/app/input-code'
import ButtonBlue from 'components/ui/button/_color/_blue'
import CellHeading from 'components/ui/cell/__heading'
import { GoogleCheck2faCode } from 'components/ui/google-check-2fa-code'
import Popup from 'components/ui/popup'
import { getHeadings } from 'components/ui/account/account-info/add-second-cred-popup/utils'

import styles from './styles.module.scss'
import { useTranslation } from 'utils/use-translation'

export const AddSecondCredPopup: FC<{
  closePopup: () => void
  hasPhone: boolean
  isEdit?: boolean
  isBackupEmail?: boolean
}> = ({ closePopup, hasPhone, isEdit, isBackupEmail }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const validator = useRef<ValidatorWrapper>()
  const emailVerifiedToken = useRef('')
  const phoneVerifiedToken = useRef('')
  const emailVerificationCode = useRef('')
  const phoneVerificationCode = useRef('')
  const isActive2faRef = useRef(false)

  const [isLoading, _setIsLoading] = useState(false)
  const [phoneOrEmail, _setPhoneOrEmail] = useState('')

  const isLoadingRef = useRef(isLoading)
  const phoneOrEmailRef = useRef(phoneOrEmail)

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [isVisibleErrors, setIsVisibleErrors] = useState(false)
  const [isPhoneOrEmailValid, setIsPhoneOrEmailValid] = useState<boolean>(null)
  const [phoneCode, setPhoneCode] = useState('')
  const [emailCode, setEmailCode] = useState('')
  const [isPhoneCodeValid, setIsPhoneCodeValid] = useState<boolean>(null)
  const [isEmailCodeValid, setIsEmailCodeValid] = useState<boolean>(null)
  const [isValid2faCode, setIsValid2faCode] = useState(null)
  const [rateLimitErr, setRateLimitErr] = useState<string>(null)
  const [errorMessage, setErrorMessage] = useState('')

  const setIsLoading = (value: boolean): void => {
    isLoadingRef.current = value
    _setIsLoading(value)
  }

  const setPhoneOrEmail = (value: string): void => {
    phoneOrEmailRef.current = value
    _setPhoneOrEmail(value)
  }

  const onChange = (value: string): void => {
    setIsVisibleErrors(false)
    setErrorMessage('')
    setPhoneOrEmail(value)
  }

  const verificationCode = hasPhone ? phoneVerificationCode.current : emailVerificationCode.current

  const resendPhoneCode = async (captcha: string): Promise<void> => {
    const token = await resendVerificationCode({
      verification_token: verificationCode,
      captcha: captcha,
    })

    if (hasPhone) {
      phoneVerificationCode.current = token
    } else {
      emailVerificationCode.current = token
    }
  }

  const resendEmailCode = async (captcha: string): Promise<void> => {
    const token = await resendVerificationCode({
      verification_token: verificationCode,
      captcha: captcha,
    })

    if (hasPhone) {
      emailVerificationCode.current = token
    } else {
      phoneVerificationCode.current = token
    }
  }

  const validatePhoneOrEmail = async (captcha: string): Promise<boolean> => {
    if (!phoneOrEmailRef.current) {
      setIsPhoneOrEmailValid(false)
      setIsVisibleErrors(true)
      return false
    }

    setIsLoading(true)
    setIsVisibleErrors(false)

    if (isBackupEmail) {
      const { tokens, isActive2fa, error } = await sendVerificationCode({
        ...(hasPhone ? { email: phoneOrEmailRef.current } : { phone: phoneOrEmailRef.current }),
        captcha: captcha,
        type: EMessageTypes.SET_FALLBACK_EMAIL,
      })
      isActive2faRef.current = isActive2fa
      emailVerificationCode.current = tokens[0]

      const result = !!emailVerificationCode.current

      if (result) {
        setStep(2)
      }

      setIsVisibleErrors(!result)
      setIsPhoneOrEmailValid(result)
      setIsLoading(false)

      if (error?.includes('limit')) {
        setErrorMessage(t('rate_limit_exceeded_retry_later'))
      }

      return result
    } else {
      const { tokens, isActive2fa, error } = await sendVerificationCode({
        ...(hasPhone ? { email: phoneOrEmailRef.current } : { phone: phoneOrEmailRef.current }),
        captcha: captcha,
        type: EMessageTypes.SECOND_CREDENTIAL,
      })
      isActive2faRef.current = isActive2fa
      phoneVerificationCode.current = tokens[0]
      emailVerificationCode.current = tokens[1]

      const result = !!emailVerificationCode.current && !!phoneVerificationCode.current

      if (result) {
        setStep(2)
      }

      setIsVisibleErrors(!result)
      setIsPhoneOrEmailValid(result)
      setIsLoading(false)

      if (error?.includes('limit')) {
        setErrorMessage(t('rate_limit_exceeded_retry_later'))
      }

      return result
    }
  }

  const validateEmailCode = async () => {
    if (emailCode?.length !== constants.codeLength) {
      setIsEmailCodeValid(false)
      setIsVisibleErrors(true)
      return
    }

    try {
      const res = await getVerifiedToken({
        code: emailCode,
        verification_token: emailVerificationCode.current,
      })
      if (res.result) {
        emailVerifiedToken.current = res.response
        setIsVisibleErrors(!emailVerifiedToken.current || !phoneVerifiedToken.current)
        setIsEmailCodeValid(!!emailVerifiedToken.current)
      } else {
        setIsVisibleErrors(true)
        setIsEmailCodeValid(false)
      }
    } catch (err) {
      const msg = err?.response?.message || err?.response?.error || ''

      if (err.response && msg.includes('limit')) {
        setRateLimitErr(t('rate_limit_exceeded_retry_later'))
        setIsVisibleErrors(true)
        setIsEmailCodeValid(false)
      }
    }
  }

  const validatePhoneCode = async () => {
    if (phoneCode?.length !== constants.codeLength) {
      setIsPhoneCodeValid(false)
      setIsVisibleErrors(true)
      return
    }

    try {
      const res = await getVerifiedToken({
        code: phoneCode,
        verification_token: phoneVerificationCode.current,
      })
      if (res.result) {
        phoneVerifiedToken.current = res.response
        setIsVisibleErrors(!emailVerifiedToken.current || !phoneVerifiedToken.current)
        setIsPhoneCodeValid(!!phoneVerifiedToken.current)
      } else {
        setIsVisibleErrors(true)
        setIsPhoneCodeValid(false)
      }
    } catch (err) {
      const msg = err?.response?.message || err?.response?.error || ''

      if (err.response && msg.includes('limit')) {
        setRateLimitErr(t('rate_limit_exceeded_retry_later'))
        setIsVisibleErrors(true)
        setIsPhoneCodeValid(false)
      }
    }
  }

  const validateCodes = async (): Promise<void> => {
    if (isLoadingRef.current) {
      return
    }

    setIsLoading(true)

    if (isBackupEmail) {
      await Promise.all([validateEmailCode()])
    } else {
      await Promise.all([validateEmailCode(), validatePhoneCode()])
    }

    setIsLoading(false)
  }

  const confirmUpdate = async (): Promise<void> => {
    if (isBackupEmail) {
      if (!isEmailCodeValid) {
        return
      }
    } else {
      if (!isEmailCodeValid || !isPhoneCodeValid) {
        return
      }
    }

    if (isActive2faRef.current) {
      setStep(3)
    } else {
      await onCode2faSubmit(null)
    }
  }

  const onCode2faSubmit = async (code2fa) => {
    setIsLoading(true)

    let result = null
    if (isBackupEmail) {
      const { res } = await setFallbackEmail({
        verification_token: [emailVerifiedToken.current],
        code_2fa: code2fa,
        new_email: phoneOrEmail,
      })

      result = res
    } else {
      result = await updateUser({
        verification_token: [emailVerifiedToken.current, phoneVerifiedToken.current],
        code_2fa: code2fa,
      })
    }

    if (result) {
      await dispatch(fetchUser())
      closePopup()
    } else {
      setIsValid2faCode(false)
      setIsVisibleErrors(true)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setIsPhoneCodeValid(null)

    if (phoneCode?.length !== constants.codeLength || isLoadingRef.current) {
      return
    }

    async function runValidatePhoneCode() {
      setIsLoading(true)
      await validatePhoneCode()
      setIsLoading(false)
    }

    void runValidatePhoneCode()
  }, [phoneCode])

  useEffect(() => {
    setIsEmailCodeValid(null)

    if (emailCode?.length !== constants.codeLength || isLoadingRef.current) {
      return
    }

    async function runValidateEmailCode() {
      setIsLoading(true)
      await validateEmailCode()
      setIsLoading(false)
    }

    void runValidateEmailCode()
  }, [emailCode])

  return (
    <ValidatorWrapper ref={validator}>
      <Popup
        onClose={closePopup}
        crossIconBtnStyle={styles.crossIcon}
        heading={t(getHeadings(step, hasPhone, isEdit, isBackupEmail).title)}
        subtitle={t(getHeadings(step, hasPhone, isEdit, isBackupEmail).subtitle)}
      >
        {step === 1 && (
          <>
            <div className={styles.phoneEmail}>
              <div className={styles.fieldWrapper}>
                {hasPhone && (
                  <ValidatorField value={phoneOrEmail} rules={validatorRules.email}>
                    <CellHeading>{isBackupEmail ? t('your_backup_email') : t('user_email')}</CellHeading>
                    <InputEmail
                      onChange={onChange}
                      isValid={isPhoneOrEmailValid}
                      isVisibleError={isVisibleErrors}
                      value={phoneOrEmail}
                      errorMessage={errorMessage}
                    />
                  </ValidatorField>
                )}

                {!hasPhone && (
                  <ValidatorField value={phoneOrEmail} ru={validatorRules.bool}>
                    <CellHeading className={styles.input__label}>{t('your_phone')}</CellHeading>
                    <InputPhone
                      value={phoneOrEmail}
                      onChange={onChange}
                      isValid={isPhoneOrEmailValid}
                      isVisibleError={isVisibleErrors}
                      errorMessage={errorMessage}
                    />
                  </ValidatorField>
                )}
              </div>
            </div>
            <div className={cn(styles.captchaWrapper, styles.verifyButtonContainer)}>
              <Captcha onCaptchaSuccess={validatePhoneOrEmail} setIsLoading={setIsLoading}>
                <ButtonBlue className={styles.verifyButton} isLoading={isLoading}>{t('verify')}</ButtonBlue>
              </Captcha>
            </div>
          </>
        )}

        {step === 2 && (
          <div className={styles.codes}>
            <div className={styles.codeRequestWrapper}>
              {!isBackupEmail && (
                <div className={styles.fieldWrapper}>
                  <ValidatorField value={phoneCode} rules={rules.bool} />
                  <CellHeading className={styles.input__label}>{t('enter_sms_code')}</CellHeading>
                  <InputCode
                    onEnter={validateCodes}
                    isValid={isPhoneCodeValid}
                    value={phoneCode}
                    onChange={(code: string) => {
                      setPhoneCode(code)
                      setRateLimitErr(null)
                    }}
                    onResendCode={resendPhoneCode}
                    isVisibleError={isVisibleErrors}
                    disabled={isPhoneCodeValid === true}
                    errorCode={rateLimitErr}
                  />
                </div>
              )}

              <div className={styles.fieldWrapper}>
                <ValidatorField value={emailCode} rules={rules.bool} />
                <CellHeading className={styles.input__label}>{t('enter_email_code')}</CellHeading>
                <InputCode
                  onEnter={validateCodes}
                  isValid={isEmailCodeValid}
                  value={emailCode}
                  onChange={(code: string) => {
                    setEmailCode(code)
                    setRateLimitErr(null)
                  }}
                  onResendCode={resendEmailCode}
                  isVisibleError={isVisibleErrors}
                  disabled={isEmailCodeValid === true}
                />
              </div>
            </div>

            <div className={cn(styles.captchaWrapper, styles.verifyButtonContainer)}>
              {(isEmailCodeValid && isPhoneCodeValid && !isBackupEmail) || (isEmailCodeValid && isBackupEmail) ? (
                <ButtonBlue className={styles.verifyButton} onClick={confirmUpdate}>{t('confirm')}</ButtonBlue>
              ) : (
                <ButtonBlue isLoading={isLoading} className={styles.verifyButton} onClick={validateCodes}>
                  {hasPhone ? (isBackupEmail ? t('add_backup_email') : t('add_email')) : t('add_phone')}
                </ButtonBlue>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <GoogleCheck2faCode
            classNameInputCode={styles.inputCode2fa}
            onSubmit={onCode2faSubmit}
            isCodeValid={isValid2faCode}
            isLoading={isLoading}
            setIsValidCode={(v) => setIsValid2faCode(v)}
          />
        )}
      </Popup>
    </ValidatorWrapper>
  )
}
